import React, { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import { districtDisplayLabel } from "../utils/geoDirectorySelectOptions";
import { storedDistrictMatchesDirectoryKey } from "../data/geoDirectoryStore";
import {
  REPORT_STATUS_LABELS,
  applicationsInVillage,
  districtReportRows,
  geoReportCsvHeaders,
  geoReportRowToCsvCells,
  mandalReportRows,
  villageReportRows,
  type GeoReportRow,
} from "../utils/geoReportStats";
import { normalizeGeoLabel } from "../utils/geoLabel";
import { downloadCsv } from "../utils/csvDownload";
import { fetchLeadsTable, type LeadTableRow } from "../api/leadsTableApi";
import { AddLeadModal, type AddLeadTarget } from "../components/AddLeadModal";
import { leadHoStatusKind, leadHoStatusShortLabel, leadSheetInterestDisplayLabel, leadSheetInterestKind } from "../utils/leadTableRowFilter";
import type { Application } from "../types";

type ReportTab = "overview" | "leads" | "applicants" | "beneficiaries";

function reportTabLabel(tab: ReportTab): string {
  if (tab === "overview") return "Overview";
  return tab.charAt(0).toUpperCase() + tab.slice(1);
}

type ViewState =
  | { screen: "districts" }
  | { screen: "mandals"; district: string }
  | { screen: "villages"; district: string; mandal: string }
  | { screen: "applicants"; district: string; mandal: string; village: string };

function viewFromSearch(params: URLSearchParams): ViewState {
  const dRaw = params.get("district");
  const mRaw = params.get("mandal");
  const vRaw = params.get("village");
  const d = dRaw?.trim() ?? null;
  const m = mRaw?.trim() ?? null;
  const v = vRaw?.trim() ?? null;
  if (d && m && v) return { screen: "applicants", district: d, mandal: m, village: v };
  if (d && m) return { screen: "villages", district: d, mandal: m };
  if (d) return { screen: "mandals", district: d };
  return { screen: "districts" };
}

function reportsUrl(parts: { district?: string; mandal?: string; village?: string }): string {
  const q = new URLSearchParams();
  if (parts.district) q.set("district", parts.district);
  if (parts.mandal) q.set("mandal", parts.mandal);
  if (parts.village) q.set("village", parts.village);
  const s = q.toString();
  return s ? `/reports/area?${s}` : "/reports/area";
}

function districtLabel(store: ReturnType<typeof useGeoDirectory>["store"], key: string): string {
  return districtDisplayLabel(store, key);
}

function statusLabelForCsv(status: Application["status"]): string {
  return REPORT_STATUS_LABELS[status];
}

export function GeographicReports() {
  const { applications, milestoneTemplates } = useAppState();
  const { store, loading: geoLoading, error: geoError, refetch: refetchGeo } = useGeoDirectory();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const view = useMemo(() => viewFromSearch(searchParams), [searchParams]);

  const [reportTab, setReportTab] = useState<ReportTab>("overview");
  const [leads, setLeads] = useState<LeadTableRow[]>([]);

  useEffect(() => {
    fetchLeadsTable().then(setLeads).catch(() => setLeads([]));
  }, []);

  const [areaSearch, setAreaSearch] = useState("");

  const [showAddLeadModal, setShowAddLeadModal] = useState(false);

  const applicationsForReport = applications;

  const rowsRaw: GeoReportRow[] = useMemo(() => {
    if (!store) return [];
    if (view.screen === "districts") return districtReportRows(applicationsForReport, store);
    if (view.screen === "mandals") return mandalReportRows(applicationsForReport, view.district, store);
    if (view.screen === "villages")
      return villageReportRows(applicationsForReport, view.district, view.mandal, store);
    return [];
  }, [applicationsForReport, view, store]);

  const rows: GeoReportRow[] = useMemo(() => {
    const q = areaSearch.trim().toLowerCase();
    let r = rowsRaw;
    if (q) r = r.filter((row) => row.label.toLowerCase().includes(q));
    return r;
  }, [rowsRaw, areaSearch]);

  const hasActiveTableFilters = areaSearch.trim() !== "";

  function clearTableFilters() {
    setAreaSearch("");
  }

  const directoryKpis = useMemo(() => {
    if (!store) return { districts: 0, mandals: 0, villages: 0 };
    if (view.screen === "districts") {
      const t = store.tgDirectoryAggregateCounts();
      return {
        districts: t.districts,
        mandals: t.mandals,
        villages: t.villages,
      };
    }
    if (view.screen === "mandals") {
      const t = store.tgDirectoryCountsForDistrict(view.district);
      return { districts: 1, mandals: t.mandals, villages: t.villages };
    }
    if (view.screen === "villages") {
      const v = store.tgVillageCountForMandal(view.district, view.mandal);
      return { districts: 1, mandals: 1, villages: v };
    }
    return { districts: 0, mandals: 0, villages: 0 };
  }, [store, view]);

  const applicantRows = useMemo(() => {
    if (view.screen !== "applicants") return [];
    return applicationsInVillage(applications, view.district, view.mandal, view.village, store);
  }, [applications, view]);

  const leadsForView = useMemo(() => {
    let f = leads;
    if (!store) return f;
    if (view.screen === "mandals" || view.screen === "villages" || view.screen === "applicants") {
      f = f.filter((l) => storedDistrictMatchesDirectoryKey(store, view.district, l.district));
    }
    if (view.screen === "villages" || view.screen === "applicants") {
      const mn = normalizeGeoLabel(view.mandal);
      f = f.filter((l) => normalizeGeoLabel(l.mandal ?? "") === mn);
    }
    if (view.screen === "applicants") {
      const vn = normalizeGeoLabel(view.village);
      f = f.filter((l) => normalizeGeoLabel(l.village ?? "") === vn);
    }
    return f;
  }, [leads, view]);

  const appsForView = useMemo(() => {
    let f = applicationsForReport;
    if (!store) return f;
    if (view.screen === "mandals" || view.screen === "villages" || view.screen === "applicants") {
      f = f.filter((a) => storedDistrictMatchesDirectoryKey(store, view.district, a.district));
    }
    if (view.screen === "villages" || view.screen === "applicants") {
      const mn = normalizeGeoLabel(view.mandal);
      f = f.filter((a) => normalizeGeoLabel(a.mandal ?? "") === mn);
    }
    if (view.screen === "applicants") {
      const vn = normalizeGeoLabel(view.village);
      f = f.filter((a) => normalizeGeoLabel(a.village ?? "") === vn);
    }
    return f;
  }, [applicationsForReport, view]);

  const leadsKpis = useMemo(() => {
    const total = leadsForView.length;
    const approved = leadsForView.filter((l) => leadHoStatusKind(l.hoStatus) === "approved").length;
    const rejected = leadsForView.filter((l) => leadHoStatusKind(l.hoStatus) === "reject").length;
    const onHold = leadsForView.filter((l) => leadHoStatusKind(l.hoStatus) === "on_hold").length;
    return { total, approved, rejected, onHold };
  }, [leadsForView]);

  const applicantsKpis = useMemo(() => {
    const total = appsForView.length;
    const verificationPending = appsForView.filter((a) => a.status === "pending_validation").length;
    const readyForIciciMou = appsForView.filter(
      (a) => a.status === "ready_for_icici" || a.status === "ready_for_mou",
    ).length;
    const mouCompleted = appsForView.filter((a) => a.status === "icici_recorded" || a.status === "subcontractor_linked").length;
    return { total, verificationPending, readyForIciciMou, mouCompleted };
  }, [appsForView]);

  // Milestone template IDs sorted by sortOrder — used to map M1/M2/M3/M4 correctly
  const sortedMilestoneIds = useMemo(
    () => [...milestoneTemplates].sort((a, b) => a.sortOrder - b.sortOrder).map((t) => t.id),
    [milestoneTemplates],
  );

  const beneficiariesKpis = useMemo(() => {
    const total = appsForView.length;
    const awaitingSubcontractor = appsForView.filter((a) => a.status === "icici_recorded").length;
    const subcontracted = appsForView.filter((a) => a.status === "subcontractor_linked").length;
    const workInitiation = appsForView.filter((a) => Boolean(a.plannedStartDate)).length;
    const milestoneCompleted = (idx: number) => {
      const tid = sortedMilestoneIds[idx];
      if (!tid) return 0;
      return appsForView.filter((a) =>
        a.milestoneProgress.find((m) => m.templateId === tid)?.milestoneCompletedAt != null,
      ).length;
    };
    return {
      total, awaitingSubcontractor, subcontracted, workInitiation,
      milestone1: milestoneCompleted(0),
      milestone2: milestoneCompleted(1),
      milestone3: milestoneCompleted(2),
      milestone4: milestoneCompleted(3),
    };
  }, [appsForView, sortedMilestoneIds]);

  // Per-row leads stats for the table
  const leadsRowsRaw = useMemo(() => {
    if (!store) return [];
    function buildLeadRow(key: string, label: string, sub: LeadTableRow[]) {
      const approved = sub.filter((l) => leadHoStatusKind(l.hoStatus) === "approved").length;
      const rejected = sub.filter((l) => leadHoStatusKind(l.hoStatus) === "reject").length;
      const onHold = sub.filter((l) => leadHoStatusKind(l.hoStatus) === "on_hold").length;
      return { key, label, total: sub.length, approved, rejected, onHold };
    }
    if (view.screen === "districts") {
      return store.tgRegistrationDistricts().map((d) => {
        const sub = leads.filter((l) => storedDistrictMatchesDirectoryKey(store, d.districtKey, l.district));
        return buildLeadRow(d.districtKey, districtDisplayLabel(store, d.districtKey), sub);
      });
    }
    if (view.screen === "mandals") {
      const sub = leads.filter((l) => storedDistrictMatchesDirectoryKey(store, view.district, l.district));
      const dir = store.tgMandalsForDistrict(view.district);
      const byNorm = new Map<string, LeadTableRow[]>();
      for (const l of sub) {
        const nk = normalizeGeoLabel(l.mandal ?? "");
        if (!byNorm.has(nk)) byNorm.set(nk, []);
        byNorm.get(nk)!.push(l);
      }
      const rows: ReturnType<typeof buildLeadRow>[] = [];
      const used = new Set<string>();
      for (const m of dir) {
        const nk = normalizeGeoLabel(m.mandalName);
        rows.push(buildLeadRow(m.mandalName, m.mandalName, byNorm.get(nk) ?? []));
        used.add(nk);
      }
      for (const [nk, list] of byNorm) {
        if (!nk || used.has(nk)) continue;
        const label = list[0].mandal?.trim() || "Unspecified mandal";
        rows.push(buildLeadRow(label, label, list));
      }
      return rows.sort((a, b) => a.label.localeCompare(b.label, "en"));
    }
    if (view.screen === "villages") {
      const mn = normalizeGeoLabel(view.mandal);
      const sub = leads.filter(
        (l) =>
          storedDistrictMatchesDirectoryKey(store, view.district, l.district ?? "") &&
          normalizeGeoLabel(l.mandal ?? "") === mn,
      );
      const dir = store.tgVillagesForMandal(view.district, view.mandal);
      const byNorm = new Map<string, LeadTableRow[]>();
      for (const l of sub) {
        const nk = normalizeGeoLabel(l.village ?? "");
        if (!byNorm.has(nk)) byNorm.set(nk, []);
        byNorm.get(nk)!.push(l);
      }
      const rows: ReturnType<typeof buildLeadRow>[] = [];
      const used = new Set<string>();
      for (const v of dir) {
        const nk = normalizeGeoLabel(v.name);
        rows.push(buildLeadRow(v.name, v.name, byNorm.get(nk) ?? []));
        used.add(nk);
      }
      for (const [nk, list] of byNorm) {
        if (!nk || used.has(nk)) continue;
        const label = list[0].village?.trim() || "Unspecified village";
        rows.push(buildLeadRow(label, label, list));
      }
      return rows.sort((a, b) => a.label.localeCompare(b.label, "en"));
    }
    return [];
  }, [leads, view, store]);

  // Per-row beneficiary + milestone stats for the table
  const beneficiaryRowsRaw = useMemo(() => {
    if (!store) return [];
    return rowsRaw.map((row) => {
      let appsInRow: Application[];
      if (view.screen === "districts") {
        appsInRow = applicationsForReport.filter(
          (a) => storedDistrictMatchesDirectoryKey(store, row.key, a.district),
        );
      } else if (view.screen === "mandals") {
        const nk = normalizeGeoLabel(row.key);
        appsInRow = applicationsForReport.filter(
          (a) =>
            storedDistrictMatchesDirectoryKey(store, view.district, a.district) &&
            normalizeGeoLabel(a.mandal ?? "") === nk,
        );
      } else if (view.screen === "villages") {
        const mn = normalizeGeoLabel(view.mandal);
        const vk = normalizeGeoLabel(row.key);
        appsInRow = applicationsForReport.filter(
          (a) =>
            storedDistrictMatchesDirectoryKey(store, view.district, a.district) &&
            normalizeGeoLabel(a.mandal ?? "") === mn &&
            normalizeGeoLabel(a.village ?? "") === vk,
        );
      } else {
        appsInRow = [];
      }
      return {
        ...row,
        awaitingSubcontractor: appsInRow.filter((a) => a.status === "icici_recorded").length,
        subcontracted: appsInRow.filter((a) => a.status === "subcontractor_linked").length,
        workInitiation: appsInRow.filter((a) => Boolean(a.plannedStartDate)).length,
        milestone1: (() => { const tid = sortedMilestoneIds[0]; return tid ? appsInRow.filter((a) => a.milestoneProgress.find((m) => m.templateId === tid)?.milestoneCompletedAt != null).length : 0; })(),
        milestone2: (() => { const tid = sortedMilestoneIds[1]; return tid ? appsInRow.filter((a) => a.milestoneProgress.find((m) => m.templateId === tid)?.milestoneCompletedAt != null).length : 0; })(),
        milestone3: (() => { const tid = sortedMilestoneIds[2]; return tid ? appsInRow.filter((a) => a.milestoneProgress.find((m) => m.templateId === tid)?.milestoneCompletedAt != null).length : 0; })(),
        milestone4: (() => { const tid = sortedMilestoneIds[3]; return tid ? appsInRow.filter((a) => a.milestoneProgress.find((m) => m.templateId === tid)?.milestoneCompletedAt != null).length : 0; })(),
      };
    });
  }, [rowsRaw, applicationsForReport, view, sortedMilestoneIds]);

  const leadsRows = useMemo(() => {
    const q = areaSearch.trim().toLowerCase();
    if (!q) return leadsRowsRaw;
    return leadsRowsRaw.filter((r) => r.label.toLowerCase().includes(q));
  }, [leadsRowsRaw, areaSearch]);

  const beneficiaryRows = useMemo(() => {
    const q = areaSearch.trim().toLowerCase();
    if (!q) return beneficiaryRowsRaw;
    return beneficiaryRowsRaw.filter((r) => r.label.toLowerCase().includes(q));
  }, [beneficiaryRowsRaw, areaSearch]);

  const applicantRowsRaw = useMemo(() => {
    return rowsRaw.map((row) => ({
      ...row,
      verificationPending: row.byStatus.pending_validation,
      readyForIciciMou: row.byStatus.ready_for_icici + row.byStatus.ready_for_mou,
      mouCompleted: row.byStatus.icici_recorded + row.byStatus.subcontractor_linked,
    }));
  }, [rowsRaw]);

  const applicantTableRows = useMemo(() => {
    const q = areaSearch.trim().toLowerCase();
    if (!q) return applicantRowsRaw;
    return applicantRowsRaw.filter((r) => r.label.toLowerCase().includes(q));
  }, [applicantRowsRaw, areaSearch]);

  const activeRawRows =
    reportTab === "overview"
      ? []
      : reportTab === "leads"
        ? leadsRowsRaw
        : reportTab === "applicants"
          ? applicantRowsRaw
          : beneficiaryRowsRaw;
  const activeRows =
    reportTab === "overview"
      ? []
      : reportTab === "leads"
        ? leadsRows
        : reportTab === "applicants"
          ? applicantTableRows
          : beneficiaryRows;

  const pendingLeads =
    leadsKpis.total - leadsKpis.approved - leadsKpis.rejected - leadsKpis.onHold;
  const onHoldCount = useMemo(() => appsForView.filter((a) => a.status === "on_hold").length, [appsForView]);

  /** High-level snapshot for the Overview tab (same geographic scope as other tabs). */
  const overviewTotals = useMemo(
    () => ({
      totalLeads: leadsKpis.total,
      totalApplicants: applicantsKpis.total,
      totalBeneficiaries: appsForView.filter((a) => a.status === "subcontractor_linked").length,
    }),
    [leadsKpis.total, applicantsKpis.total, appsForView],
  );

  const title =
    view.screen === "districts"
      ? "Area reports — District"
      : view.screen === "mandals"
        ? `Area reports — Mandals (${districtLabel(store, view.district)})`
        : view.screen === "villages"
          ? `Area reports — Villages (${view.mandal})`
          : `Applicants — ${view.village}`;

  const breadcrumb = useMemo(() => {
    if (view.screen === "districts") {
      return [{ label: "Home", to: "/" }, { label: "Reports Hub", to: "/reports" }, { label: "Area reports" }];
    }
    if (view.screen === "mandals") {
      return [
        { label: "Home", to: "/" },
        { label: "Reports Hub", to: "/reports" },
        { label: "Area reports", to: "/reports/area" },
        { label: districtLabel(store, view.district) },
      ];
    }
    if (view.screen === "villages") {
      return [
        { label: "Home", to: "/" },
        { label: "Reports Hub", to: "/reports" },
        { label: "Area reports", to: "/reports/area" },
        { label: districtLabel(store, view.district), to: reportsUrl({ district: view.district }) },
        { label: view.mandal },
      ];
    }
    return [
      { label: "Home", to: "/" },
      { label: "Reports Hub", to: "/reports" },
      { label: "Area reports", to: "/reports/area" },
      { label: districtLabel(store, view.district), to: reportsUrl({ district: view.district }) },
      { label: view.mandal, to: reportsUrl({ district: view.district, mandal: view.mandal }) },
      { label: view.village },
    ];
  }, [view]);

  function goBack() {
    if (view.screen === "mandals") navigate("/reports/area");
    else if (view.screen === "villages") navigate(reportsUrl({ district: view.district }));
    else if (view.screen === "applicants")
      navigate(reportsUrl({ district: view.district, mandal: view.mandal }));
  }

  function downloadAggregateCsv() {
    const areaCol =
      view.screen === "districts" ? "District" : view.screen === "mandals" ? "Mandal" : "Village";
    const h = geoReportCsvHeaders(areaCol);
    const dataRows = rows.map(geoReportRowToCsvCells);
    const name =
      view.screen === "districts"
        ? "report-districts"
        : view.screen === "mandals"
          ? `report-mandals-${view.district}`
          : `report-villages-${view.district}-${view.mandal.replace(/\s+/g, "_")}`;
    downloadCsv(`${name}.csv`, h, dataRows);
  }

  function downloadApplicantsCsv() {
    if (view.screen !== "applicants") return;
    const h = [
      "Application ID",
      "Mobile app ref",
      "Beneficiary",
      "Father / husband",
      "District",
      "Mandal",
      "Village",
      "Status",
      "Received (UTC)",
      "House sanction",
    ];
    const data = applicantRows.map((a) => [
      a.id,
      a.mobile.applicationId,
      a.mobile.beneficiaryName,
      a.mobile.fatherOrHusbandName,
      districtLabel(store, a.district),
      a.mandal,
      a.village,
      statusLabelForCsv(a.status),
      a.receivedAt,
      a.mobile.houseSanctionNumber,
    ]);
    downloadCsv(
      `report-applicants-${view.district}-${view.mandal}-${view.village}.csv`.replace(/\s+/g, "_"),
      h,
      data,
    );
  }

  if (geoError) {
    return (
      <>
        <PageHeader
          breadcrumb={[{ label: "Home", to: "/" }, { label: "Reports Hub", to: "/reports" }, { label: "Area reports" }]}
          title="Area reports"
          subtitle="Geographic directory could not be loaded"
        />
        <div className="card card--elevated geo-reports" style={{ padding: "1.25rem 1.5rem" }}>
          <p className="geo-reports__cell-main">The district–mandal–village master is served from the API database.</p>
          <p className="text-muted" style={{ marginTop: "0.5rem" }}>
            {geoError}
          </p>
          <p className="text-muted" style={{ marginTop: "0.75rem" }}>
            Run the API (<code>npm run dev</code> from the repo root) with <code>server/.env</code> pointing at
            MariaDB/MySQL, or open <code>http://localhost:4000/api/geo/directory</code> to verify. If you use MySQL,
            run <code>npm run seed:geo:mysql -w server</code> once to create and fill <code>geo_directory_*</code>{" "}
            tables.
          </p>
          <button type="button" className="btn btn--primary btn--sm" style={{ marginTop: "1rem" }} onClick={() => void refetchGeo()}>
            Retry
          </button>
        </div>
      </>
    );
  }

  if (geoLoading || !store) {
    return (
      <>
        <PageHeader
          breadcrumb={[{ label: "Home", to: "/" }, { label: "Reports Hub", to: "/reports" }, { label: "Area reports" }]}
          title="Area reports"
          subtitle="Loading geographic directory from server…"
        />
        <div className="card card--elevated geo-reports" style={{ padding: "1.25rem 1.5rem" }}>
          <p className="text-muted">Fetching district, mandal, and village data from the database.</p>
        </div>
      </>
    );
  }

  // ── helper: pct string ──────────────────────────────────────────────────────
  function pct(n: number, base: number): string {
    if (!base) return "—";
    return `${Math.round((n / base) * 100)}%`;
  }

  // ── navigate helper for table & top-areas clicks ─────────────────────────
  function drillInto(key: string) {
    if (view.screen === "districts") navigate(reportsUrl({ district: key }));
    else if (view.screen === "mandals") navigate(reportsUrl({ district: view.district, mandal: key }));
    else if (view.screen === "villages") navigate(reportsUrl({ district: view.district, mandal: view.mandal, village: key }));
  }

  // ── scope breadcrumb labels ───────────────────────────────────────────────
  const scopeCrumbs: { label: string; active: boolean }[] =
    view.screen === "districts"
      ? [
          { label: "Telangana", active: false },
          { label: `${directoryKpis.districts} Districts`, active: false },
          { label: `${directoryKpis.mandals} Mandals`, active: false },
          { label: `${directoryKpis.villages} Villages`, active: true },
        ]
      : view.screen === "mandals"
        ? [
            { label: "Telangana", active: false },
            { label: districtLabel(store, view.district), active: false },
            { label: `${directoryKpis.mandals} Mandals`, active: false },
            { label: `${directoryKpis.villages} Villages`, active: true },
          ]
        : view.screen === "villages"
          ? [
              { label: "Telangana", active: false },
              { label: districtLabel(store, view.district), active: false },
              { label: view.mandal, active: false },
              { label: `${directoryKpis.villages} Villages`, active: true },
            ]
          : [];

  const showScope = view.screen !== "applicants" && scopeCrumbs.length > 0;
  const scopeNode = showScope ? (
    <div className="bi2-scope-bar bi2-scope-bar--inline" aria-label="Geographic scope">
      {scopeCrumbs.map((crumb, i) => (
        <span key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {i > 0 && <span className="bi2-scope-sep">/</span>}
          <span className={`bi2-scope-crumb${crumb.active ? " bi2-scope-crumb--active" : ""}`}>
            {crumb.label}
          </span>
        </span>
      ))}
    </div>
  ) : null;

  return (
    <>
      {/* ── Page header ───────────────────────────────────────────────── */}
      <PageHeader
        breadcrumb={breadcrumb.map((c) => (c.to ? { label: c.label, to: c.to } : { label: c.label }))}
        title={title}
        subtitle={
          view.screen === "applicants" ? "Dossiers in this village. Open a row to work the file." : undefined
        }
        actions={
          (view.screen !== "districts" || showScope) ? (
            <div className="geo-reports__actions">
              {scopeNode}
              {view.screen !== "districts" ? (
                <button type="button" className="btn btn--secondary btn--sm" onClick={goBack}>
                  ← Back
                </button>
              ) : null}
            </div>
          ) : undefined
        }
      />

      {/* ── Tab switcher — always visible ─────────────────────────── */}
      <div className="bi2-tabs" role="tablist" aria-label="Report category">
        {(["overview", "leads", "applicants", "beneficiaries"] as ReportTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={reportTab === tab}
            className={`bi2-tab${reportTab === tab ? " bi2-tab--active" : ""}`}
            onClick={() => setReportTab(tab)}
          >
            {reportTabLabel(tab)}
          </button>
        ))}
      </div>

      {/* ── KPI grid — always visible ─────────────────────────────── */}
      {(() => {
        return (
          <div className="bi2-kpi-grid" aria-label={`${reportTab} KPIs`} role="tabpanel">
            {reportTab === "overview" ? (
              <>
                <div className="bi2-kpi" style={{ "--kpi-color": "#0f172a" } as CSSProperties}>
                  <div className="bi2-kpi__num">{overviewTotals.totalLeads.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Total Leads</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#2563eb" } as CSSProperties}>
                  <div className="bi2-kpi__num">{overviewTotals.totalApplicants.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Total Applicants</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#15803d" } as CSSProperties}>
                  <div className="bi2-kpi__num">{overviewTotals.totalBeneficiaries.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Total Beneficiaries</div>
                  <p className="text-muted" style={{ margin: "0.35rem 0 0", fontSize: "0.7rem", lineHeight: 1.35, fontWeight: 500 }}>
                    With subcontractor linked (construction stage)
                  </p>
                </div>
              </>
            ) : reportTab === "leads" ? (
              <>
                <div className="bi2-kpi" style={{ "--kpi-color": "#0f172a" } as CSSProperties}>
                  <div className="bi2-kpi__num">{leadsKpis.total.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Total Leads</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#16a34a" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#dcfce7", color: "#16a34a" }}>{pct(leadsKpis.approved, leadsKpis.total)}</div>
                  <div className="bi2-kpi__num">{leadsKpis.approved.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Approved</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#dc2626" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#fee2e2", color: "#dc2626" }}>{pct(leadsKpis.rejected, leadsKpis.total)}</div>
                  <div className="bi2-kpi__num">{leadsKpis.rejected.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Rejected</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#d97706" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#fef3c7", color: "#d97706" }}>{pct(leadsKpis.onHold, leadsKpis.total)}</div>
                  <div className="bi2-kpi__num">{leadsKpis.onHold.toLocaleString()}</div>
                  <div className="bi2-kpi__label">On hold</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#64748b" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#f1f5f9", color: "#64748b" }}>{pct(pendingLeads, leadsKpis.total)}</div>
                  <div className="bi2-kpi__num">{pendingLeads.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Pending</div>
                </div>
              </>
            ) : reportTab === "applicants" ? (
              <>
                <div className="bi2-kpi" style={{ "--kpi-color": "#0f172a" } as CSSProperties}>
                  <div className="bi2-kpi__num">{applicantsKpis.total.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Total Applicants</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#d97706" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#fef3c7", color: "#d97706" }}>{pct(applicantsKpis.verificationPending, applicantsKpis.total)}</div>
                  <div className="bi2-kpi__num">{applicantsKpis.verificationPending.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Verif. Pending</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#0891b2" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#cffafe", color: "#0891b2" }}>{pct(applicantsKpis.readyForIciciMou, applicantsKpis.total)}</div>
                  <div className="bi2-kpi__num">{applicantsKpis.readyForIciciMou.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Ready ICICI &amp; MOU</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#16a34a" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#dcfce7", color: "#16a34a" }}>{pct(applicantsKpis.mouCompleted, applicantsKpis.total)}</div>
                  <div className="bi2-kpi__num">{applicantsKpis.mouCompleted.toLocaleString()}</div>
                  <div className="bi2-kpi__label">MOU Completed</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#dc2626" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#fee2e2", color: "#dc2626" }}>{pct(onHoldCount, applicantsKpis.total)}</div>
                  <div className="bi2-kpi__num">{onHoldCount.toLocaleString()}</div>
                  <div className="bi2-kpi__label">On Hold</div>
                </div>
              </>
            ) : (
              <>
                <div className="bi2-kpi" style={{ "--kpi-color": "#0f172a" } as CSSProperties}>
                  <div className="bi2-kpi__num">{beneficiariesKpis.total.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Total Beneficiaries</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#d97706" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#fef3c7", color: "#d97706" }}>{pct(beneficiariesKpis.awaitingSubcontractor, beneficiariesKpis.total)}</div>
                  <div className="bi2-kpi__num">{beneficiariesKpis.awaitingSubcontractor.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Awaiting SC</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#0891b2" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#cffafe", color: "#0891b2" }}>{pct(beneficiariesKpis.subcontracted, beneficiariesKpis.total)}</div>
                  <div className="bi2-kpi__num">{beneficiariesKpis.subcontracted.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Subcontracted</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#7c3aed" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#ede9fe", color: "#7c3aed" }}>{pct(beneficiariesKpis.workInitiation, beneficiariesKpis.total)}</div>
                  <div className="bi2-kpi__num">{beneficiariesKpis.workInitiation.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Work Initiation</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#4ade80" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#dcfce7", color: "#16a34a" }}>{pct(beneficiariesKpis.milestone1, beneficiariesKpis.total)}</div>
                  <div className="bi2-kpi__num">{beneficiariesKpis.milestone1.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Milestone 1</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#22c55e" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#dcfce7", color: "#16a34a" }}>{pct(beneficiariesKpis.milestone2, beneficiariesKpis.total)}</div>
                  <div className="bi2-kpi__num">{beneficiariesKpis.milestone2.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Milestone 2</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#16a34a" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#dcfce7", color: "#16a34a" }}>{pct(beneficiariesKpis.milestone3, beneficiariesKpis.total)}</div>
                  <div className="bi2-kpi__num">{beneficiariesKpis.milestone3.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Milestone 3</div>
                </div>
                <div className="bi2-kpi" style={{ "--kpi-color": "#15803d" } as CSSProperties}>
                  <div className="bi2-kpi__pct" style={{ background: "#dcfce7", color: "#15803d" }}>{pct(beneficiariesKpis.milestone4, beneficiariesKpis.total)}</div>
                  <div className="bi2-kpi__num">{beneficiariesKpis.milestone4.toLocaleString()}</div>
                  <div className="bi2-kpi__label">Milestone 4</div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* ── Data table (all screens) ──────────────────────────────────── */}
      <div className="bi2-table-card">
        {reportTab === "overview" ? (
          <div className="geo-reports__overview-placeholder" role="region" aria-label="Detail tables">
            <p className="geo-reports__overview-placeholder-title">Area breakdown &amp; lists</p>
            <p className="text-muted" style={{ margin: "0.25rem 0 0", maxWidth: "40rem" }}>
              Use <strong>Leads</strong>, <strong>Applicants</strong>, or <strong>Beneficiaries</strong> to open the
              heatmap table and row-level data for the same area.
            </p>
          </div>
        ) : view.screen === "applicants" ? (
          /* ── Village detail: tab-aware ─────────────────────────────── */
          <>
            {/* Add Lead button (leads tab only) */}
            {reportTab === "leads" ? (
              <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid #e2e8f0" }}>
                <button
                  type="button"
                  className="btn btn--primary btn--sm btn--tg"
                  onClick={() => setShowAddLeadModal(true)}
                >
                  + Add Lead
                </button>
              </div>
            ) : null}

            {/* Village leads table */}
            {reportTab === "leads" ? (
              <div className="table-wrap">
                <table className="data-table geo-reports__table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Aadhaar</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadsForView.length === 0 ? (
                      <tr><td colSpan={5} className="data-table__empty">No leads in this village yet.</td></tr>
                    ) : (
                      leadsForView.map((l) => (
                        <tr key={l.leadId}>
                          <td style={{ fontWeight: 600 }}>{l.applicantName || "—"}</td>
                          <td className="mono">{l.mobileNumber || "—"}</td>
                          <td className="mono text-muted">{l.aadhaarNumber || "—"}</td>
                          <td>
                            <div className="lead-status-cell">
                              {leadHoStatusKind(l.hoStatus) !== "none" ? (
                                <span
                                  className={`lead-status-pill lead-status-pill--${leadHoStatusKind(l.hoStatus)}`}
                                  style={{ fontSize: "0.6875rem" }}
                                >
                                  {leadHoStatusShortLabel(l.hoStatus)}
                                </span>
                              ) : null}
                              {(() => {
                                const sk = leadSheetInterestKind(l.status);
                                const lab = leadSheetInterestDisplayLabel(l.status);
                                if (sk === "interested") {
                                  return (
                                    <span className="lead-status-pill lead-status-pill--interested" style={{ fontSize: "0.6875rem" }}>
                                      {lab}
                                    </span>
                                  );
                                }
                                if (sk === "not_interested") {
                                  return (
                                    <span className="lead-status-pill lead-status-pill--not-interested" style={{ fontSize: "0.6875rem" }}>
                                      {lab}
                                    </span>
                                  );
                                }
                                return (
                                  <span className="text-muted" style={{ fontSize: "0.6875rem", whiteSpace: "nowrap" }}>
                                    {lab}
                                  </span>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="geo-reports__num" style={{ color: "#64748b" }}>{l.createdAt?.slice(0, 10) || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : reportTab === "applicants" ? (
              /* Village applicants table */
              <div className="table-wrap">
                <table className="data-table geo-reports__table">
                  <thead>
                    <tr>
                      <th>Application</th>
                      <th>Beneficiary</th>
                      <th>Status</th>
                      <th>Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applicantRows.length === 0 ? (
                      <tr><td colSpan={4} className="data-table__empty">No applicants in this village.</td></tr>
                    ) : (
                      applicantRows.map((a) => (
                        <tr key={a.id}>
                          <td>
                            <Link to={`/applications/${a.id}/overview`} state={{ from: "applicants" }} className="geo-reports__app-link">{a.id}</Link>
                            <div className="geo-reports__muted">{a.mobile.applicationId}</div>
                          </td>
                          <td style={{ fontWeight: 600 }}>{a.mobile.beneficiaryName}</td>
                          <td>
                            <span style={{ display: "inline-block", fontSize: "0.6875rem", fontWeight: 600, padding: "0.2rem 0.55rem", borderRadius: "9999px", background: "#f1f5f9", color: "#475569", whiteSpace: "nowrap" }}>
                              {statusLabelForCsv(a.status)}
                            </span>
                          </td>
                          <td className="geo-reports__num" style={{ color: "#64748b" }}>{a.receivedAt.slice(0, 10)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Village beneficiaries table */
              (() => {
                const benRows = applicantRows.filter((a) => a.status === "icici_recorded" || a.status === "subcontractor_linked");
                return (
                  <div className="table-wrap">
                    <table className="data-table geo-reports__table">
                      <thead>
                        <tr>
                          <th>Application</th>
                          <th>Beneficiary</th>
                          <th>Pipeline Status</th>
                          <th>Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {benRows.length === 0 ? (
                          <tr><td colSpan={4} className="data-table__empty">No beneficiaries in this village yet.</td></tr>
                        ) : (
                          benRows.map((a) => (
                            <tr key={a.id}>
                              <td>
                                <Link to={`/applications/${a.id}/subcontractor`} state={{ from: "beneficiaries" }} className="geo-reports__app-link">{a.id}</Link>
                                <div className="geo-reports__muted">{a.mobile.applicationId}</div>
                              </td>
                              <td style={{ fontWeight: 600 }}>{a.mobile.beneficiaryName}</td>
                              <td>
                                <span style={{ display: "inline-block", fontSize: "0.6875rem", fontWeight: 600, padding: "0.2rem 0.55rem", borderRadius: "9999px", background: a.status === "subcontractor_linked" ? "#dcfce7" : "#fef3c7", color: a.status === "subcontractor_linked" ? "#16a34a" : "#d97706", whiteSpace: "nowrap" }}>
                                  {statusLabelForCsv(a.status)}
                                </span>
                              </td>
                              <td className="geo-reports__num" style={{ color: "#64748b" }}>{a.receivedAt.slice(0, 10)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </>
        ) : (
          <>
            {/* Toolbar — aggregate screens */}
            <div className="table-toolbar table-toolbar--stack">
              {hasActiveTableFilters ? (
                <div className="table-toolbar__top geo-reports__toolbar-clear-row">
                  <button type="button" className="btn btn--link btn--sm table-toolbar__clear" onClick={clearTableFilters}>
                    Clear filters
                  </button>
                </div>
              ) : null}
              <div className="table-filters" role="search" aria-label="Filter area report">
                <div className="field table-filters__field table-filters__field--grow">
                  <label htmlFor="geo-report-search">Search area</label>
                  <input
                    id="geo-report-search"
                    type="search"
                    placeholder={
                      view.screen === "districts"
                        ? "District name"
                        : view.screen === "mandals"
                          ? "Mandal name"
                          : "Village name"
                    }
                    value={areaSearch}
                    onChange={(e) => setAreaSearch(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="table-wrap geo-reports__table-wrap">
              <table className="data-table geo-reports__table">
                <thead>
                  <tr>
                    <th>{view.screen === "districts" ? "District" : view.screen === "mandals" ? "Mandal" : "Village"}</th>
                    {reportTab === "leads" ? (
                      <>
                        <th className="geo-reports__num geo-reports__th-wrap">Total</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">Approved</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">Rejected</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">On hold</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">Pending</th>
                      </>
                    ) : reportTab === "applicants" ? (
                      <>
                        <th className="geo-reports__num geo-reports__th-wrap">Total</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">Verif. Pending</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">ICICI &amp; MOU</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">MOU Done</th>
                      </>
                    ) : (
                      <>
                        <th className="geo-reports__num geo-reports__th-wrap">Total</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">Await SC</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">Subctd.</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">Work Init.</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">M1</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">M2</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">M3</th>
                        <th className="geo-reports__num geo-reports__stat geo-reports__th-wrap">M4</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {activeRawRows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="data-table__empty">
                        No data recorded at this level yet.
                      </td>
                    </tr>
                  ) : activeRows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="data-table__empty">
                        No rows match the current filters.
                      </td>
                    </tr>
                  ) : reportTab === "leads" ? (() => {
                    const maxTotal = Math.max(...leadsRows.map((r) => r.total), 1);
                    return leadsRows.map((row) => {
                      const isEmpty = row.total === 0;
                      const pendingRow = row.total - row.approved - row.rejected - row.onHold;
                      return (
                        <tr
                          key={row.key}
                          className={"geo-reports__row--click" + (isEmpty ? " bi2-row--dim" : "")}
                          onClick={() => drillInto(row.key)}
                        >
                          <td>
                            <span className="geo-reports__cell-main">{row.label}</span>
                            {!isEmpty ? (
                              <span className="geo-reports__hint"> ↗</span>
                            ) : (
                              <span className="geo-reports__hint geo-reports__hint--empty"> No data</span>
                            )}
                          </td>
                          <td
                            className="geo-reports__num bi2-cell-heat"
                            style={{ background: `rgba(15,23,42,${(row.total / maxTotal) * 0.14})` }}
                          >
                            {row.total}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.approved > 0 ? `rgba(22,163,74,${(row.approved / (row.total || 1)) * 0.22})` : undefined,
                              color: row.approved > 0 ? "#16a34a" : "#94a3b8",
                            }}
                          >
                            {row.approved}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.rejected > 0 ? `rgba(220,38,38,${(row.rejected / (row.total || 1)) * 0.22})` : undefined,
                              color: row.rejected > 0 ? "#dc2626" : "#94a3b8",
                            }}
                          >
                            {row.rejected}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.onHold > 0 ? `rgba(217,119,6,${(row.onHold / (row.total || 1)) * 0.22})` : undefined,
                              color: row.onHold > 0 ? "#d97706" : "#94a3b8",
                            }}
                          >
                            {row.onHold}
                          </td>
                          <td className="geo-reports__num geo-reports__stat" style={{ color: "#64748b" }}>
                            {pendingRow}
                          </td>
                        </tr>
                      );
                    });
                  })() : reportTab === "applicants" ? (() => {
                    const maxTotal = Math.max(...applicantTableRows.map((r) => r.total), 1);
                    return applicantTableRows.map((row) => {
                      const isEmpty = row.total === 0;
                      return (
                        <tr
                          key={row.key}
                          className={"geo-reports__row--click" + (isEmpty ? " bi2-row--dim" : "")}
                          onClick={() => drillInto(row.key)}
                        >
                          <td>
                            <span className="geo-reports__cell-main">{row.label}</span>
                            {!isEmpty ? (
                              <span className="geo-reports__hint"> ↗</span>
                            ) : (
                              <span className="geo-reports__hint geo-reports__hint--empty"> No data</span>
                            )}
                          </td>
                          <td
                            className="geo-reports__num bi2-cell-heat"
                            style={{ background: `rgba(15,23,42,${(row.total / maxTotal) * 0.14})` }}
                          >
                            {row.total}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.verificationPending > 0 ? `rgba(217,119,6,${(row.verificationPending / (row.total || 1)) * 0.22})` : undefined,
                              color: "#d97706",
                            }}
                          >
                            {row.verificationPending}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.readyForIciciMou > 0 ? `rgba(8,145,178,${(row.readyForIciciMou / (row.total || 1)) * 0.22})` : undefined,
                              color: "#0891b2",
                            }}
                          >
                            {row.readyForIciciMou}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.mouCompleted > 0 ? `rgba(22,163,74,${(row.mouCompleted / (row.total || 1)) * 0.22})` : undefined,
                              color: "#16a34a",
                            }}
                          >
                            {row.mouCompleted}
                          </td>
                        </tr>
                      );
                    });
                  })() : (() => {
                    const maxTotal = Math.max(...beneficiaryRows.map((r) => r.total), 1);
                    return beneficiaryRows.map((row) => {
                      const isEmpty = row.total === 0;
                      return (
                        <tr
                          key={row.key}
                          className={"geo-reports__row--click" + (isEmpty ? " bi2-row--dim" : "")}
                          onClick={() => drillInto(row.key)}
                        >
                          <td>
                            <span className="geo-reports__cell-main">{row.label}</span>
                            {!isEmpty ? (
                              <span className="geo-reports__hint"> ↗</span>
                            ) : (
                              <span className="geo-reports__hint geo-reports__hint--empty"> No data</span>
                            )}
                          </td>
                          <td
                            className="geo-reports__num bi2-cell-heat"
                            style={{ background: `rgba(15,23,42,${(row.total / maxTotal) * 0.14})` }}
                          >
                            {row.total}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.awaitingSubcontractor > 0 ? `rgba(217,119,6,${(row.awaitingSubcontractor / (row.total || 1)) * 0.22})` : undefined,
                              color: "#d97706",
                            }}
                          >
                            {row.awaitingSubcontractor}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.subcontracted > 0 ? `rgba(8,145,178,${(row.subcontracted / (row.total || 1)) * 0.22})` : undefined,
                              color: "#0891b2",
                            }}
                          >
                            {row.subcontracted}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.workInitiation > 0 ? `rgba(124,58,237,${(row.workInitiation / (row.total || 1)) * 0.22})` : undefined,
                              color: "#7c3aed",
                            }}
                          >
                            {row.workInitiation}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.milestone1 > 0 ? `rgba(74,222,128,${(row.milestone1 / (row.total || 1)) * 0.22})` : undefined,
                              color: "#16a34a",
                            }}
                          >
                            {row.milestone1}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.milestone2 > 0 ? `rgba(34,197,94,${(row.milestone2 / (row.total || 1)) * 0.22})` : undefined,
                              color: "#16a34a",
                            }}
                          >
                            {row.milestone2}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.milestone3 > 0 ? `rgba(22,163,74,${(row.milestone3 / (row.total || 1)) * 0.22})` : undefined,
                              color: "#16a34a",
                            }}
                          >
                            {row.milestone3}
                          </td>
                          <td
                            className="geo-reports__num geo-reports__stat bi2-cell-heat"
                            style={{
                              background: row.milestone4 > 0 ? `rgba(21,128,61,${(row.milestone4 / (row.total || 1)) * 0.22})` : undefined,
                              color: "#15803d",
                            }}
                          >
                            {row.milestone4}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Add Lead modal — village + leads tab */}
      {showAddLeadModal && view.screen === "applicants" ? (
        <AddLeadModal
          target={{ district: districtLabel(store, view.district), mandal: view.mandal, village: view.village } satisfies AddLeadTarget}
          onClose={() => setShowAddLeadModal(false)}
          onSaved={() => fetchLeadsTable().then(setLeads).catch(() => {})}
        />
      ) : null}
    </>
  );
}
